import { BrowserUIReact } from "@jsplumbtoolkit/browser-ui-react";
import { Node } from "@jsplumbtoolkit/core";
import { InheritanceStatus, QuestInterface, QuestType } from "@swivl-mono/types";
import { Button, ButtonColor, Input, Select, SelectItem } from "@swivl-mono/ui";
import _ from "lodash";
import React from "react";
import { RiCheckboxMultipleBlankLine } from "react-icons/ri";

import { QuestsDropdown } from "../../../../../Components/Dropdowns/QuestsDropdown/QuestsDropdown";
import { useSwivlNavigate } from "../../../../../Models/NavBar/useSwivlNavigate";
import { QuestModelHelper } from "../../../../../Models/Quests/Helpers/QuestModel.Helper";
import { QuestsModel } from "../../../../../Models/Quests/Quests.Model";
import { QuestDataFromPlumb } from "../../Canvas/Methods/QuestDataFromPlumb";
import NodeModel from "../../Nodes/NodeModel";
import "./MultiSelectPanel.scss";

interface Props {
  toolkit: BrowserUIReact;
  selectedNodes: Node[];
  deleteNodes(nodeOrNodes: Node | Node[]): void;
  duplicateNodes(nodeIds: string[]): void;
  duplicateNodesToQuest?(nodeIds: string[], targetQuestSlug: string): Promise<void>;
  quest: QuestInterface;
  saveQuest(): void;
  canEdit: boolean;
}
interface MergeProps {
  toolkit: BrowserUIReact;
  quest: QuestInterface;
  selectedNodes: Node[];
  cancelMerge(): void;
  deleteNodes(nodeOrNodes: Node | Node[]): void;
  saveQuest(): void;
}

interface CrossQuestDuplicateProps {
  quest: QuestInterface;
  selectedNodes: Node[];
  cancelDuplicate(): void;
  duplicateNodesToQuest(nodeIds: string[], targetQuestSlug: string): Promise<void>;
}

function MergeQuestView(props: MergeProps) {
  const { selectedNodes, quest, cancelMerge, toolkit, deleteNodes } = props;
  const [newQuestName, setNewQuestName] = React.useState("New Quest");
  const journeys = QuestsModel.useState((s) => s.journeys);
  const [journeySlug, setJourneySlug] = React.useState(quest.journeySlug);
  const [isCreating, setIsCreating] = React.useState(false);
  const navigate = useSwivlNavigate();

  const createQuest = async () => {
    setIsCreating(true);
    var data = toolkit.exportData();
    let allNodes = QuestDataFromPlumb(data);
    let ids = selectedNodes.map((n) => n.id);
    let nodes: any = {};

    _.forIn(allNodes, (value, key) => {
      if (ids.includes(key)) {
        nodes[key] = value;
      }
    });
    let newQuest = QuestModelHelper.newQuest(newQuestName, QuestType.NORMAL, quest.journeySlug!, InheritanceStatus.NONE, quest.order + 1);
    newQuest.data = { ...newQuest.data, ...nodes };
    const newQ = await QuestsModel.createQuest(newQuest);
    let averageLeft = 0;
    let averageTop = 0;

    selectedNodes.forEach((n) => {
      averageLeft += n.data.left;
      averageTop += n.data.top;
    });
    averageLeft = averageLeft / selectedNodes.length;
    averageTop = averageTop / selectedNodes.length;

    deleteNodes(selectedNodes);
    const model = NodeModel.forType("quest");
    let questNode = model.Empty();
    questNode.questSlug = newQ.slug;
    let newPlumbNode = model.PlumbData(questNode).node;
    newPlumbNode.left = averageLeft;
    newPlumbNode.top = averageTop;
    toolkit.addNode(newPlumbNode);

    await props.saveQuest();

    navigate("/quest/" + newQ.slug);
  };

  return (
    <div className="content">
      <h3>Merge {selectedNodes.length} Nodes into New Quest</h3>
      <Input
        label="Quest Name"
        placeholder="New quest name..."
        value={newQuestName}
        onChange={(e) => setNewQuestName(e.target.value)}
        containerClassName="mb-4"
      />
      <Select
        label="Journey"
        options={Object.values(journeys!).map(
          (i): SelectItem => ({
            label: i.name,
            value: i.slug
          })
        )}
        selectedValues={journeySlug ? [journeySlug] : []}
        hideClear
        onChange={(vals) => setJourneySlug(vals[0])}
      />
      <Button color={ButtonColor.ORANGE} fullWidth onClick={createQuest} loading={isCreating}>
        Save
      </Button>
      <Button color={ButtonColor.BLACK} fullWidth onClick={cancelMerge}>
        Cancel
      </Button>
      <hr />
      Clicking save will create a new quest with the selected nodes merged into it. The nodes in this quest will be replaced with a Quest node.
    </div>
  );
}

function CrossQuestDuplicateView(props: CrossQuestDuplicateProps) {
  const { selectedNodes, quest, cancelDuplicate, duplicateNodesToQuest } = props;
  const [selectedQuestSlug, setSelectedQuestSlug] = React.useState<string>("");
  const [isDuplicating, setIsDuplicating] = React.useState(false);

  // Filter function to exclude current quest only
  const filterQuest = React.useCallback(
    (targetQuest: QuestInterface) => {
      return targetQuest.slug !== quest.slug;
    },
    [quest.slug]
  );

  const duplicateToQuest = async () => {
    if (!selectedQuestSlug) return;

    setIsDuplicating(true);
    try {
      const nodeIds = selectedNodes.map((n) => n.id);
      await duplicateNodesToQuest(nodeIds, selectedQuestSlug);
    } catch (error) {
      console.error("Failed to duplicate nodes to quest:", error);
      setIsDuplicating(false);
    }
  };

  // Get quest name from the selected slug for display
  const quests = QuestsModel.useState((s) => s.quests);
  const selectedQuestName = React.useMemo(() => {
    if (!selectedQuestSlug || !quests) return selectedQuestSlug;
    return quests[selectedQuestSlug]?.name || selectedQuestSlug;
  }, [selectedQuestSlug, quests]);

  return (
    <div className="content">
      <h3>Duplicate {selectedNodes.length} Nodes to Existing Quest</h3>

      <QuestsDropdown
        label="Target Quest"
        value={selectedQuestSlug}
        filterQuest={filterQuest}
        questSelected={(slug) => {
          setSelectedQuestSlug(slug);
        }}
      />

      {selectedQuestSlug && (
        <>
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffecb5",
              borderRadius: "4px",
              padding: "12px",
              margin: "16px 0",
              fontSize: "14px",
              color: "#856404"
            }}
          >
            ⚠️ You will be navigated to the target quest. Save your changes before proceeding.
          </div>

          <Button color={ButtonColor.ORANGE} fullWidth onClick={duplicateToQuest} loading={isDuplicating}>
            Duplicate to {selectedQuestName}
          </Button>
        </>
      )}

      <Button color={ButtonColor.BLACK} fullWidth onClick={cancelDuplicate}>
        Cancel
      </Button>

      <hr style={{ margin: "16px 0" }} />

      <div style={{ fontSize: "14px", color: "#666", lineHeight: "1.4" }}>
        Selected nodes will be copied to the target quest. Start nodes will be excluded. Navigation gotos pointing outside the selection will be
        cleared.
      </div>
    </div>
  );
}

export function MultiSelectPanel(props: Props) {
  const { selectedNodes, deleteNodes, duplicateNodes, duplicateNodesToQuest, toolkit, canEdit } = props;
  const [showMerge, setShowMerge] = React.useState(false);
  const [showCrossQuestDuplicate, setShowCrossQuestDuplicate] = React.useState(false);

  const handleDuplicateInQuest = () => {
    const nodeIds = selectedNodes.map((n) => n.id);
    duplicateNodes(nodeIds);
  };

  return (
    <div className="MultiSelectPanel">
      {showMerge ? (
        <MergeQuestView
          selectedNodes={selectedNodes}
          quest={props.quest}
          toolkit={toolkit}
          deleteNodes={deleteNodes}
          saveQuest={props.saveQuest}
          cancelMerge={() => {
            setShowMerge(false);
          }}
        />
      ) : showCrossQuestDuplicate ? (
        <CrossQuestDuplicateView
          selectedNodes={selectedNodes}
          quest={props.quest}
          duplicateNodesToQuest={duplicateNodesToQuest!}
          cancelDuplicate={() => {
            setShowCrossQuestDuplicate(false);
          }}
        />
      ) : (
        <div className="content">
          <span className="icon">
            <RiCheckboxMultipleBlankLine />
          </span>
          <h3>{selectedNodes.length} Nodes Selected</h3>
          {canEdit && (
            <>
              <Button color={ButtonColor.ORANGE} fullWidth onClick={handleDuplicateInQuest}>
                Duplicate in This Quest
              </Button>
              {duplicateNodesToQuest && (
                <Button
                  color={ButtonColor.ORANGE}
                  fullWidth
                  onClick={() => {
                    setShowCrossQuestDuplicate(true);
                  }}
                >
                  Duplicate to Existing Quest
                </Button>
              )}
              <Button
                color={ButtonColor.ORANGE}
                fullWidth
                onClick={() => {
                  setShowMerge(true);
                }}
              >
                Merge To Quest
              </Button>
            </>
          )}

          {canEdit && (
            <Button
              fullWidth
              onClick={() => {
                deleteNodes(selectedNodes);
              }}
            >
              Delete
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
